import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { PageRoot, PageHeader, KpiGrid, KpiCard, Toolbar } from '../ui/Page';
import { SearchInput } from '../ui/SearchInput';
import { DataTable, dash, type DataTableColumn } from '@/components/ui/data-table';
import { useServerTable } from '../ui/useServerTable';
import { useWorkspaceTable } from '../ui/useWorkspaceTable';
import { useUrlParam, workspaceId } from '../ui/pageTabs';
import { StatusBadge } from '../ui/Badge';
import { Sheet, SheetOpenFull } from '../ui/Sheet';
import { EmptyState } from '../ui/EmptyState';
import {
  ErrorState,
  FiguresNotice,
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { PageSkeleton } from '../ui/Skeleton';
import { Button } from '../ui/Button';
import { NewQuoteSheet } from '../create/NewQuoteSheet';
import { IconPlus } from '../icons';
import { QUOTE_STATUS, display } from '../status';
import { money, moneyShort } from '../money';
import { IconQuotes } from '../icons';
import { QUOTE_STATES, type QuoteState } from '@servwell/shared';
import { QuoteFamilyGroups } from '../jobs/JobQuotesPanel';
import { field } from '../create/Field';
import { BackCrumb } from '../ui/Detail';

export function QuotesPage() {
  const trpc = useTRPC();
  const workflow = useQuery(trpc.jobPipeline.list.queryOptions());
  const state = shownState(workflow, workflow.data);
  if (state !== 'ready')
    return (
      <PageRoot>
        <PageHeader title="Quotes" />
        <UnresolvedState
          state={state}
          icon={IconQuotes}
          what="your quote workspace"
          error={workflow.error}
          onRetry={() => void workflow.refetch()}
        />
      </PageRoot>
    );
  return (
    <>
      <StaleNotice
        show={refreshFailed(workflow, workflow.data) || workflow.isPaused}
        onRetry={() => void workflow.refetch()}
      />
      {workflow.data?.enabled ? <GroupedQuotesPage /> : <LegacyQuotesPage />}
    </>
  );
}

function GroupedQuotesPage() {
  const trpc = useTRPC();
  const { search, setSearch, query, table, setParams } = useWorkspaceTable(20);
  const [status] = useUrlParam<QuoteState | ''>(
    'status',
    (value) =>
      QUOTE_STATES.includes(value as QuoteState) ? (value as QuoteState) : undefined,
    '',
  );
  const [ownerUserId] = useUrlParam('ownerUserId', workspaceId, '');
  const [pipelineId] = useUrlParam('pipelineId', workspaceId, '');
  const members = useQuery(trpc.org.members.queryOptions());
  const pipelines = useQuery(trpc.jobPipeline.list.queryOptions());
  const [creating, setCreating] = useState(false);
  const q = useQuery(
    trpc.quote.workspaceList.queryOptions({
      ...query,
      status: status || undefined,
      ownerUserId: ownerUserId || undefined,
      pipelineId: pipelineId || undefined,
    }),
  );
  const state = shownState(q, q.data);
  return (
    <PageRoot>
      <BackCrumb to="/jobs" label="Jobs" />
      <PageHeader
        title="Quotes"
        sub="Independent quotes and revisions, grouped by Job"
        right={
          <Button onClick={() => setCreating(true)}>
            <IconPlus size={15} /> New quote
          </Button>
        }
      />
      <Toolbar>
        <SearchInput
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search Jobs or quotes"
          aria-label="Search Jobs or quotes"
        />
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          Status
          <select
            className={field}
            aria-label="Quote status"
            value={status}
            onChange={(event) => setParams({ status: event.target.value })}
          >
            <option value="">All statuses</option>
            {QUOTE_STATES.map((value) => (
              <option key={value} value={value}>
                {value === 'approved' ? 'Accepted' : display(QUOTE_STATUS, value).label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          Job owner
          <select
            className={field}
            aria-label="Job owner"
            value={ownerUserId}
            onChange={(event) => setParams({ ownerUserId: event.target.value })}
          >
            <option value="">All owners</option>
            {ownerUserId &&
            !members.data?.some((member) => member.userId === ownerUserId) ? (
              <option value={ownerUserId}>Selected owner unavailable</option>
            ) : null}
            {(members.data ?? []).map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.name ?? member.email}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          Pipeline
          <select
            className={field}
            aria-label="Job pipeline"
            value={pipelineId}
            onChange={(event) => setParams({ pipelineId: event.target.value })}
          >
            <option value="">All pipelines</option>
            {pipelineId &&
            !pipelines.data?.pipelines.some((pipeline) => pipeline.id === pipelineId) ? (
              <option value={pipelineId}>Selected pipeline unavailable</option>
            ) : null}
            {(pipelines.data?.pipelines ?? []).map((pipeline) => (
              <option key={pipeline.id} value={pipeline.id}>
                {pipeline.label}
                {pipeline.active ? '' : ' (retired)'}
              </option>
            ))}
          </select>
        </label>
        <Link to="/jobs" className="text-sm font-semibold hover:underline">
          All Jobs
        </Link>
      </Toolbar>
      <StaleNotice
        show={refreshFailed(q, q.data) || q.isPaused}
        onRetry={() => void q.refetch()}
      />
      {state !== 'ready' ? (
        <UnresolvedState
          state={state}
          icon={IconQuotes}
          what="your grouped quotes"
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      ) : q.data ? (
        <>
          {q.data.rows.length ? (
            <QuoteFamilyGroups
              groups={q.data.rows}
              status={status || undefined}
              search={query.search}
              ownerUserId={ownerUserId || undefined}
              pipelineId={pipelineId || undefined}
            />
          ) : (
            <EmptyState
              icon={IconQuotes}
              title={
                search || status || ownerUserId || pipelineId
                  ? 'No matching quotes'
                  : 'No quotes yet'
              }
              description="Create a quote inside a Job to keep its scope, revisions and work orders together."
            />
          )}
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
            <Button
              size="sm"
              variant="ghost"
              disabled={query.offset === 0 || q.isFetching}
              onClick={() => table(q.data.total).onPage(query.offset / query.limit - 1)}
            >
              Previous
            </Button>
            <span>
              {q.data.total} Jobs with matching quotes · page{' '}
              {query.offset / query.limit + 1}
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={query.offset + query.limit >= q.data.total || q.isFetching}
              onClick={() => table(q.data.total).onPage(query.offset / query.limit + 1)}
            >
              Next
            </Button>
          </div>
        </>
      ) : null}
      <NewQuoteSheet open={creating} onClose={() => setCreating(false)} />
    </PageRoot>
  );
}

type Quote = {
  id: string;
  quoteNumber: number | null;
  customerId: string;
  /** Joined by the server — see the note on JobsPage. */
  customerName: string | null;
  title: string | null;
  status: string;
  totalCents: number;
  createdAt: string | Date;
};

function LegacyQuotesPage() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const { search, setSearch, query, table } = useServerTable();
  const quotes = useQuery({
    ...trpc.quote.list.queryOptions(query),
    placeholderData: (prev) => prev,
  });
  /**
   * The header numbers are aggregates over EVERY quote, so they cannot be reduced from the
   * page on screen. They used to be `rows.filter(...)` over one server page — see
   * `services/quote.ts quoteSummary` for what that read on a shop past the page size.
   */
  const summary = useQuery(trpc.quote.summary.queryOptions());
  const [preview, setPreview] = useState<Quote | null>(null);
  const [creating, setCreating] = useState(false);

  if (quotes.isPending) return <PageSkeleton />;
  if (quotes.isError) {
    return (
      <PageRoot>
        <PageHeader title="Quotes" />
        <ErrorState
          icon={IconQuotes}
          what="quotes"
          onRetry={() => void quotes.refetch()}
        />
      </PageRoot>
    );
  }

  const rows = quotes.data.rows as Quote[];

  const columns: DataTableColumn<Quote>[] = [
    {
      key: 'quote',
      label: 'Quote',
      value: (q) => q.quoteNumber ?? 0,
      cell: (q) => (
        <div>
          <p className="font-medium">{q.title ?? 'Untitled quote'}</p>
          <p className="font-mono text-xs text-muted-foreground">
            {q.quoteNumber ? `#${q.quoteNumber}` : 'Draft'}
          </p>
        </div>
      ),
    },
    {
      key: 'customer',
      label: 'Customer',
      value: (q) => q.customerName,
      cell: (q) => dash(q.customerName),
    },
    {
      key: 'status',
      label: 'Status',
      value: (q) => display(QUOTE_STATUS, q.status).label,
      cell: (q) => {
        const s = display(QUOTE_STATUS, q.status);
        return <StatusBadge tone={s.tone}>{s.label}</StatusBadge>;
      },
    },
    {
      key: 'total',
      label: 'Total',
      value: (q) => q.totalCents,
      searchValue: (q) => money(q.totalCents),
      cell: (q) => money(q.totalCents),
      align: 'right',
    },
    {
      key: 'created',
      label: 'Created',
      value: (q) => new Date(q.createdAt).getTime(),
      searchValue: (q) => new Date(q.createdAt).toLocaleDateString(),
      cell: (q) => new Date(q.createdAt).toLocaleDateString(),
      align: 'right',
    },
  ];

  return (
    <PageRoot>
      <PageHeader
        title="Quotes"
        right={
          <Button onClick={() => setCreating(true)}>
            <IconPlus size={15} />
            New quote
          </Button>
        }
      />
      {/* `—` in the tiles is honest and was a dead end: nothing here could ask again, so a
          failed aggregate left four blanks until some unrelated refetch happened to fix them. */}
      <FiguresNotice
        show={summary.isError && summary.data === undefined}
        what="your quote totals"
        onRetry={() => void summary.refetch()}
      />
      <KpiGrid>
        {/* `—` until the aggregate lands, exactly as Money does. A 0 here would read as
            "nothing is waiting on a customer", which is the reason anyone opens this page. */}
        <KpiCard label="Awaiting answer" value={summary.data?.outstandingCount ?? '—'} />
        <KpiCard
          label="Value out"
          value={summary.data ? moneyShort(summary.data.outstandingCents) : '—'}
          tone="brand"
        />
        <KpiCard
          label="Approved"
          value={summary.data?.approvedCount ?? '—'}
          tone="success"
        />
        <KpiCard
          label="Won value"
          value={summary.data ? moneyShort(summary.data.approvedCents) : '—'}
          tone="success"
        />
      </KpiGrid>
      <Toolbar>
        <SearchInput
          placeholder="Search quotes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search quotes"
        />
      </Toolbar>
      <DataTable
        tableId="quotes"
        columns={columns}
        rows={rows}
        rowKey={(q) => q.id}
        search={search}
        server={table(quotes.data.total, quotes.isFetching)}
        onRowClick={setPreview}
        rowLabel={(q) => `Preview quote ${q.title ?? q.quoteNumber ?? ''}`}
        empty={
          <EmptyState
            icon={IconQuotes}
            title="No quotes yet"
            description="Quote a customer, and when they approve it you can turn it into a job in one click."
          />
        }
      />
      <Sheet
        open={preview !== null}
        onClose={() => setPreview(null)}
        title={preview?.title ?? 'Untitled quote'}
        badge={
          preview ? (
            <StatusBadge tone={display(QUOTE_STATUS, preview.status).tone}>
              {display(QUOTE_STATUS, preview.status).label}
            </StatusBadge>
          ) : null
        }
        footer={
          preview ? (
            <SheetOpenFull
              onClick={() =>
                navigate({ to: '/quotes/$quoteId', params: { quoteId: preview.id } })
              }
            />
          ) : null
        }
      >
        {preview ? (
          <dl className="space-y-3 text-sm">
            {(
              [
                ['Number', preview.quoteNumber ? `#${preview.quoteNumber}` : 'Draft'],
                ['Customer', preview.customerName],
                ['Total', money(preview.totalCents)],
                ['Created', new Date(preview.createdAt).toLocaleDateString()],
              ] as const
            ).map(([label, value]) => (
              <div key={label} className="flex items-baseline justify-between gap-4">
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="text-right font-medium">{dash(value)}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </Sheet>
      <NewQuoteSheet open={creating} onClose={() => setCreating(false)} />
    </PageRoot>
  );
}
